import React, { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import type { AgentInstance } from './agentTypes';
import type { UseAgentSessionReturn } from '../useAgentSession';
import type { ActivityEntry } from '../useAgentSession';
import { TokenUsageDialog } from '../TokenUsageDialog';

// ─── Status Helpers ───────────────────────────────────────────────────────

function StatusDot({
    color,
    agentStatus,
    connectionState,
}: {
    color: string;
    agentStatus: AgentInstance['agentStatus'];
    connectionState: AgentInstance['connectionState'];
}) {
    if (connectionState === 'reconnecting' || connectionState === 'connecting') {
        return <span className="inline-block w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />;
    }
    if (connectionState === 'disconnected') {
        return <span className="inline-block w-2 h-2 rounded-full bg-neutral-400" />;
    }
    switch (agentStatus) {
        case 'thinking':
        case 'tool_calling':
            return <span className="inline-block w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: color }} />;
        case 'error':
            return <span className="inline-block w-2 h-2 rounded-full bg-red-500" />;
        default:
            return <span className="inline-block w-2 h-2 rounded-full bg-green-500" />;
    }
}

function statusLabel(agent: AgentInstance): string {
    if (agent.connectionState === 'reconnecting') return 'Reconnecting...';
    if (agent.connectionState === 'connecting') return 'Connecting...';
    if (agent.connectionState === 'disconnected') return 'Disconnected';
    if (agent.sessionStatus === 'starting') return 'Starting...';
    if (agent.sessionStatus === 'paused') return 'Paused';
    if (agent.sessionStatus === 'ended') return 'Session ended';
    if (agent.sessionStatus === 'idle') return 'Ready';
    switch (agent.agentStatus) {
        case 'thinking':
            return 'Thinking...';
        case 'tool_calling':
            return 'Working...';
        case 'error':
            return 'Error';
        default:
            return 'Ready';
    }
}

// ─── Activity Entry Renderer ──────────────────────────────────────────────

function CompactActivityEntry({ entry }: { entry: ActivityEntry }) {
    switch (entry.type) {
        case 'user_message':
            return (
                <div className="px-2.5 py-1.5 rounded-lg bg-primary text-primary-foreground text-[11px] leading-snug whitespace-pre-wrap break-words self-end max-w-[85%]">
                    {entry.content}
                </div>
            );
        case 'thinking':
            return (
                <div className="px-2 py-1 text-[11px] leading-snug whitespace-pre-wrap text-neutral-500 dark:text-neutral-400 max-w-[95%]">
                    {entry.content}
                </div>
            );
        case 'tool_call':
            return (
                <div
                    className={cn(
                        'px-2 py-1 rounded text-[10px] font-mono leading-snug',
                        'bg-neutral-100/80 dark:bg-neutral-800/80',
                        'border border-neutral-200/50 dark:border-neutral-700/50',
                        'text-neutral-500 dark:text-neutral-400 whitespace-pre-wrap break-all max-w-[95%]',
                    )}
                >
                    <span className="text-amber-600 dark:text-amber-400 font-sans font-medium text-[9px] uppercase tracking-wider mr-1">
                        {entry.toolName}
                    </span>
                    {entry.content}
                </div>
            );
        case 'tool_result':
            return (
                <div
                    className={cn(
                        'ml-2 px-2 py-1 rounded text-[10px] font-mono',
                        'border-l-2 border-neutral-200 dark:border-neutral-700',
                        'text-neutral-400 dark:text-neutral-500 whitespace-pre-wrap break-all',
                        'max-h-[80px] overflow-y-auto max-w-[95%]',
                    )}
                >
                    {entry.content}
                </div>
            );
        case 'run_request':
            return (
                <div className="px-2 py-1.5 rounded-lg bg-amber-50/80 dark:bg-amber-950/20 border border-amber-200/50 dark:border-amber-800/30 text-[11px] text-amber-700 dark:text-amber-300 max-w-[95%]">
                    Run request: {entry.content}
                </div>
            );
        case 'run_status':
            return (
                <div className="px-2 py-1 rounded text-[11px] text-blue-700 dark:text-blue-300 bg-blue-50/60 dark:bg-blue-950/20 max-w-[95%]">
                    {entry.content}
                </div>
            );
        case 'error':
            return (
                <div className="px-2 py-1 rounded text-[11px] text-red-700 dark:text-red-300 bg-red-50/80 dark:bg-red-950/30 max-w-[95%]">
                    {entry.content}
                </div>
            );
        case 'debug_snapshot':
            return (
                <div className="px-2 py-1 rounded text-[10px] text-indigo-500 dark:text-indigo-400 max-w-[95%]">Debug: {entry.content}</div>
            );
    }
}

// ─── Token Display ────────────────────────────────────────────────────────

function formatTokenCount(count: number): string {
    if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
    if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
    return String(count);
}

// ─── Suggestion Prompts ──────────────────────────────────────────────────

const SUGGESTION_PROMPTS = [
    {
        label: 'Inventory webhook',
        prompt: 'Build a webhook that receives landscaping supply deliveries with item name, quantity, and supplier, validates the payload, and logs each delivery to a structured list.',
    },
    {
        label: 'Revenue report',
        prompt: 'Create a scheduled daily report that takes revenue-per-machine data from a laundromat webhook, calculates total and average revenue, and sends a summary to Slack.',
    },
    {
        label: 'Low-stock alert',
        prompt: 'Set up a webhook that receives detergent fill levels for each washing machine, checks if any are below 20%, and sends an alert with the machine IDs that need refilling.',
    },
    {
        label: 'Job scheduler',
        prompt: 'Build a workflow that takes a new landscaping job request via webhook with client name, address, and service type, then formats a confirmation message and posts it to a Slack channel.',
    },
];

// ─── Suggestion Bubble ───────────────────────────────────────────────────

function SuggestionBubble({
    label,
    prompt,
    disabled,
    onSelect,
}: {
    label: string;
    prompt: string;
    disabled: boolean;
    onSelect: () => void;
}) {
    return (
        <button
            onClick={onSelect}
            disabled={disabled}
            className={cn(
                'w-full px-2.5 py-1.5 rounded-lg text-left',
                'text-[10px]',
                'border shadow-sm',
                'bg-white/80 dark:bg-neutral-800/80',
                'backdrop-blur-sm',
                'border-neutral-200/60 dark:border-neutral-700/40',
                'text-neutral-600 dark:text-neutral-300',
                'hover:bg-white dark:hover:bg-neutral-700/80',
                'hover:shadow-md',
                'transition-all duration-150',
                'disabled:opacity-40 disabled:cursor-not-allowed',
                'group',
            )}
        >
            <span className="font-medium">{label}</span>
            <span className="block mt-0.5 text-[9px] text-neutral-400 dark:text-neutral-500 leading-snug line-clamp-2 opacity-0 max-h-0 group-hover:opacity-100 group-hover:max-h-[40px] transition-all duration-500 ease-in-out overflow-hidden">
                {prompt}
            </span>
        </button>
    );
}

// ─── Props ────────────────────────────────────────────────────────────────

interface Props {
    agent: AgentInstance;
    session: UseAgentSessionReturn | undefined;
    onDragStart: (event: React.PointerEvent<HTMLDivElement>, agentId: string, position: { x: number; y: number }) => void;
    onToggleExpanded: (agentId: string) => void;
    onRemove: (agentId: string) => void;
}

// ─── Component ────────────────────────────────────────────────────────────

export const AgentInGraphPanel: React.FC<Props> = ({ agent, session, onDragStart, onToggleExpanded, onRemove }) => {
    const [inputValue, setInputValue] = useState('');
    const [tokenDialogOpen, setTokenDialogOpen] = useState(false);
    const activityStreamRef = useRef<HTMLDivElement>(null);
    const expandedInputRef = useRef<HTMLTextAreaElement>(null);
    const compactInputRef = useRef<HTMLInputElement>(null);

    const isAgentBusy = agent.agentStatus === 'thinking' || agent.agentStatus === 'tool_calling';
    const canSend = agent.connectionState === 'connected' && inputValue.trim().length > 0;
    const hasActiveSession = agent.sessionStatus === 'active' || agent.sessionStatus === 'starting';
    const showSuggestions = agent.entries.length === 0 && !hasActiveSession;

    // Auto-scroll the activity stream to bottom when new entries arrive.
    useEffect(() => {
        const stream = activityStreamRef.current;
        if (stream) {
            stream.scrollTop = stream.scrollHeight;
        }
    }, [agent.entries]);

    // Focus the appropriate input on mount and when expand state changes
    useEffect(() => {
        // Small delay to let the DOM settle after mount/expand toggle
        const timer = setTimeout(() => {
            if (agent.isExpanded) {
                expandedInputRef.current?.focus();
            } else {
                compactInputRef.current?.focus();
            }
        }, 50);
        return () => clearTimeout(timer);
    }, [agent.isExpanded]);

    const handleSubmit = useCallback(
        (text: string) => {
            if (!session) return;
            session.submit(text);
            setInputValue('');
        },
        [session],
    );

    const handleFormSubmit = useCallback(
        (event: React.FormEvent) => {
            event.preventDefault();
            if (!canSend) return;
            handleSubmit(inputValue.trim());
        },
        [canSend, inputValue, handleSubmit],
    );

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            event.stopPropagation();
            if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                if (canSend) handleSubmit(inputValue.trim());
            }
        },
        [canSend, inputValue, handleSubmit],
    );

    const panelWidth = agent.isExpanded ? 400 : 280;
    const panelHeight = agent.isExpanded ? 420 : 'auto';

    return (
        <div
            style={{
                position: 'absolute',
                left: agent.position.x,
                top: agent.position.y,
                pointerEvents: 'auto',
                zIndex: 100,
            }}
            onClick={event => event.stopPropagation()}
            onPointerDown={event => event.stopPropagation()}
            onWheel={event => event.stopPropagation()}
        >
            {/* Main panel */}
            <div
                className={cn(
                    'flex flex-col overflow-hidden',
                    'rounded-xl border-2 shadow-lg',
                    'bg-white/90 dark:bg-neutral-900/90',
                    'backdrop-blur-xl',
                    'animate-in fade-in zoom-in-95 duration-200',
                )}
                style={{
                    width: panelWidth,
                    height: panelHeight,
                    borderColor: agent.color.light + '60',
                    boxShadow: `0 0 20px ${agent.color.light}15, 0 4px 12px rgba(0,0,0,0.1)`,
                }}
            >
                {/* Header — draggable */}
                <div
                    className={cn(
                        'flex items-center justify-between px-3 py-2 cursor-grab active:cursor-grabbing',
                        'border-b rounded-t-xl',
                        'backdrop-blur-sm',
                        'border-white/20 dark:border-neutral-700/30',
                    )}
                    style={{ backgroundColor: agent.color.primary + '20' }}
                    onPointerDown={event => {
                        if (event.button === 0) {
                            onDragStart(event, agent.agentId, agent.position);
                        }
                    }}
                >
                    <div className="flex items-center gap-2 min-w-0">
                        <StatusDot color={agent.color.light} agentStatus={agent.agentStatus} connectionState={agent.connectionState} />
                        <span className="text-[10px] text-neutral-500 dark:text-neutral-400 truncate">{statusLabel(agent)}</span>
                    </div>

                    <div className="flex items-center gap-1 flex-shrink-0">
                        {/* Model selector (before session) / indicator (during session) */}
                        {hasActiveSession ? (
                            <span className="text-[9px] text-neutral-400 dark:text-neutral-500 px-1">{agent.model}</span>
                        ) : (
                            session && (
                                <select
                                    value={agent.model}
                                    onChange={event => {
                                        event.stopPropagation();
                                        session.setModel(event.target.value as 'sonnet' | 'opus' | 'haiku');
                                    }}
                                    onPointerDown={event => event.stopPropagation()}
                                    className={cn(
                                        'text-[9px] px-1 py-0.5 rounded border',
                                        'bg-white/70 dark:bg-neutral-800/70',
                                        'border-neutral-200/50 dark:border-neutral-700/50',
                                        'text-neutral-600 dark:text-neutral-300',
                                        'focus:outline-none focus:ring-1 focus:ring-primary/30',
                                        'cursor-pointer',
                                    )}
                                >
                                    <option value="haiku">Haiku</option>
                                    <option value="sonnet">Sonnet</option>
                                    <option value="opus">Opus</option>
                                </select>
                            )
                        )}
                        {/* Expand/collapse */}
                        <button
                            onClick={event => {
                                event.stopPropagation();
                                onToggleExpanded(agent.agentId);
                            }}
                            className="p-0.5 rounded hover:bg-neutral-200/50 dark:hover:bg-neutral-700/50 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                {agent.isExpanded ? (
                                    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
                                ) : (
                                    <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                                )}
                            </svg>
                        </button>
                        {/* Close */}
                        <button
                            onClick={event => {
                                event.stopPropagation();
                                onRemove(agent.agentId);
                            }}
                            className="p-0.5 rounded hover:bg-neutral-200/50 dark:hover:bg-neutral-700/50 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M18 6L6 18M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Compact mode: just the input */}
                {!agent.isExpanded ? (
                    <div className="p-2">
                        <form onSubmit={handleFormSubmit} className="flex gap-1.5 items-center">
                            <input
                                ref={compactInputRef}
                                value={inputValue}
                                onChange={event => setInputValue(event.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder={agent.sessionStatus === 'idle' ? 'Ask the agent...' : 'Follow-up...'}
                                disabled={agent.connectionState !== 'connected'}
                                className={cn(
                                    'flex-1 px-2.5 py-1.5 text-[11px] rounded-lg border',
                                    'bg-white/70 dark:bg-neutral-900/70',
                                    'text-neutral-900 dark:text-neutral-100',
                                    'border-white/30 dark:border-neutral-700/40',
                                    'placeholder:text-neutral-400 dark:placeholder:text-neutral-500',
                                    'focus:outline-none focus:ring-1 focus:ring-primary/30',
                                    'disabled:opacity-50',
                                )}
                            />
                            <button
                                type="submit"
                                disabled={!canSend}
                                className={cn(
                                    'p-1.5 rounded-lg text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300',
                                    'disabled:opacity-30 disabled:cursor-not-allowed transition-colors',
                                )}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                                </svg>
                            </button>
                        </form>
                    </div>
                ) : (
                    /* Expanded mode: activity stream + controls + input */
                    <>
                        {/* Activity Stream */}
                        <div
                            ref={activityStreamRef}
                            className={cn(
                                'flex-1 overflow-y-auto min-h-0',
                                '[&::-webkit-scrollbar]:w-1',
                                '[&::-webkit-scrollbar-thumb]:rounded-full',
                                '[&::-webkit-scrollbar-thumb]:bg-neutral-300/40 dark:[&::-webkit-scrollbar-thumb]:bg-neutral-600/40',
                            )}
                        >
                            {agent.entries.length === 0 ? (
                                <div className="h-full flex items-center justify-center p-4">
                                    <p className="text-[12px] text-neutral-400 dark:text-neutral-500">Describe what you'd like to build.</p>
                                </div>
                            ) : (
                                <div className="p-2 flex flex-col gap-1.5">
                                    {agent.entries.map(entry => (
                                        <CompactActivityEntry key={entry.id} entry={entry} />
                                    ))}
                                    {isAgentBusy && agent.entries[agent.entries.length - 1]?.type !== 'thinking' && (
                                        <div className="px-2 py-1 text-[11px] text-neutral-400 dark:text-neutral-500 animate-pulse">
                                            {agent.agentStatus === 'thinking' ? 'Thinking...' : 'Working...'}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Controls Bar */}
                        {(hasActiveSession || agent.tokenUsage.totalInput > 0) && (
                            <div
                                className={cn(
                                    'flex items-center justify-between px-3 py-1',
                                    'border-t border-white/10 dark:border-neutral-700/20',
                                    'text-[9px] text-neutral-400 dark:text-neutral-500',
                                )}
                            >
                                <div className="flex items-center gap-2">
                                    {isAgentBusy && session && (
                                        <button
                                            onClick={() => session.cancelOperation()}
                                            className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-neutral-200/60 dark:bg-neutral-700/60 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-300/60 dark:hover:bg-neutral-600/60 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                    )}
                                    {hasActiveSession && session && (
                                        <>
                                            <label className="flex items-center gap-1 cursor-pointer select-none">
                                                <input
                                                    type="checkbox"
                                                    checked={agent.autoApproveRuns}
                                                    onChange={event => session.setAutoApproveRuns(event.target.checked)}
                                                    className="w-2.5 h-2.5 rounded accent-primary"
                                                />
                                                <span>Auto-run</span>
                                            </label>
                                            <label className="flex items-center gap-1 cursor-pointer select-none">
                                                <input
                                                    type="checkbox"
                                                    checked={agent.debugMode}
                                                    onChange={event => session.setDebugMode(event.target.checked)}
                                                    className="w-2.5 h-2.5 rounded accent-primary"
                                                />
                                                <span>Debug</span>
                                            </label>
                                        </>
                                    )}
                                </div>
                                {agent.tokenUsage.totalInput > 0 && (
                                    <button
                                        onClick={() => setTokenDialogOpen(true)}
                                        className="hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors underline decoration-dotted underline-offset-2"
                                    >
                                        {formatTokenCount(agent.tokenUsage.totalInput + agent.tokenUsage.totalOutput)} tokens
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Input Area */}
                        <div
                            className={cn(
                                'p-2 border-t rounded-b-xl',
                                'bg-white/50 dark:bg-neutral-800/50',
                                'backdrop-blur-sm',
                                'border-white/20 dark:border-neutral-700/30',
                            )}
                        >
                            <form onSubmit={handleFormSubmit} className="flex gap-1.5 items-end">
                                <textarea
                                    ref={expandedInputRef}
                                    value={inputValue}
                                    onChange={event => setInputValue(event.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder={
                                        agent.sessionStatus === 'idle' ? 'Describe what you want to build...' : 'Send a follow-up...'
                                    }
                                    disabled={agent.connectionState !== 'connected'}
                                    rows={2}
                                    className={cn(
                                        'flex-1 px-2.5 py-2 text-[12px] leading-snug font-sans',
                                        'rounded-lg border resize-none',
                                        'min-h-[44px] max-h-[100px]',
                                        'bg-white/70 dark:bg-neutral-900/70',
                                        'text-neutral-900 dark:text-neutral-100',
                                        'border-white/30 dark:border-neutral-700/40',
                                        'placeholder:text-neutral-400 dark:placeholder:text-neutral-500',
                                        'focus:outline-none focus:ring-1 focus:ring-primary/30',
                                        'disabled:opacity-50 disabled:cursor-not-allowed',
                                    )}
                                />
                                <button
                                    type="submit"
                                    disabled={!canSend}
                                    className={cn(
                                        'flex-shrink-0 p-2 rounded-lg',
                                        'bg-neutral-200 dark:bg-neutral-700',
                                        'text-neutral-700 dark:text-neutral-200',
                                        'hover:bg-neutral-300 dark:hover:bg-neutral-600',
                                        'transition-colors',
                                        'disabled:opacity-30 disabled:cursor-not-allowed',
                                    )}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                                    </svg>
                                </button>
                            </form>
                        </div>
                    </>
                )}

                <TokenUsageDialog
                    open={tokenDialogOpen}
                    onOpenChange={setTokenDialogOpen}
                    tokenUsage={agent.tokenUsage}
                    debugMode={agent.debugMode}
                />
            </div>

            {/* Floating suggestion bubbles below the panel */}
            {showSuggestions && (
                <div className="flex flex-col gap-1 mt-2 animate-in fade-in slide-in-from-top-2 duration-300" style={{ width: panelWidth }}>
                    {SUGGESTION_PROMPTS.map(suggestion => (
                        <SuggestionBubble
                            key={suggestion.label}
                            label={suggestion.label}
                            prompt={suggestion.prompt}
                            disabled={agent.connectionState !== 'connected'}
                            onSelect={() => handleSubmit(suggestion.prompt)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};
